import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClubService } from "../../../../services/clubService";
import { isClubLeaderRole } from "../../../../utils/notificationTargeting";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";

/**
 * Clubs the viewer leads (admin / e-board), mirroring AdminScreen applyAdminClubs +
 * fetchClubData (:471, :545-564): seed from DataContext.userClubs, revalidate with
 * ClubService.getUserClubs(uid), ignore superseded responses.
 */
export function useAdminClubs() {
  const { user } = useAuth();
  const { userClubs, loadUserClubs, patchUserClubMembership } = useData();
  const uid = user?.uid;
  const [remoteState, setRemoteState] = useState({ list: null, at: 0 });
  const remote = remoteState.list;
  const setRemote = useCallback((list) => setRemoteState({ list, at: Date.now() }), []);
  // When DataContext.userClubs last changed; the newer of (remote fetch, userClubs) wins so lost leadership removes access.
  const [ctxAt, setCtxAt] = useState(() => Date.now());
  useEffect(() => { setCtxAt(Date.now()); }, [userClubs]);
  const [error, setError] = useState(null);
  const [overrides, setOverrides] = useState({});
  const epoch = useRef(0);

  const refresh = useCallback(async () => {
    if (!uid) return;
    const mine = ++epoch.current;
    const { data, error: err } = await ClubService.getUserClubs(uid);
    if (epoch.current !== mine) return;
    if (err) { setError(err); return; }
    setError(null);
    setRemote(data || []);
  }, [uid, setRemote]);

  useEffect(() => {
    refresh();
    return () => { epoch.current += 1; };
  }, [refresh]);

  const adminClubs = useMemo(
    () =>
      (remote !== null && remoteState.at >= ctxAt ? remote : userClubs)
        .filter((c) => c && isClubLeaderRole(c.member_role))
        .map((c) => (overrides[c.id] ? { ...c, ...overrides[c.id] } : c)),
    [remote, remoteState.at, ctxAt, userClubs, overrides]
  );

  const loading = !error && remote === null && adminClubs.length === 0;

  /** Merge saved fields into the local copy of one club (no refetch). */
  const patchClub = useCallback((clubId, patch) => {
    setOverrides((prev) => ({ ...prev, [clubId]: { ...(prev[clubId] || {}), ...patch } }));
  }, []);

  /** After a successful admin transfer: force this club's role to "member" everywhere (AdminScreen :2395-2410). */
  const dropLeadership = useCallback(async (clubId) => {
    const refreshed = await loadUserClubs();
    const fresh = (refreshed || []).map((c) => (c?.id === clubId ? { ...c, member_role: "member" } : c));
    patchUserClubMembership(clubId, { member_role: "member" });
    epoch.current += 1; // drop any in-flight revalidation that still says admin
    setRemote(fresh);
    return fresh.filter((c) => c && isClubLeaderRole(c.member_role));
  }, [loadUserClubs, patchUserClubMembership, setRemote]);

  // True once a fresh getUserClubs list has arrived (a deep link waits for it before saying "not yours").
  const remoteLoaded = remote !== null;

  return { adminClubs, loading, remoteLoaded, error, refresh, patchClub, dropLeadership, loadUserClubs };
}
